import { useEffect, useRef } from "react";

/**
 * Polls `fn` every `intervalMs` while the browser tab is visible.
 * Pauses immediately when the tab is hidden, and fires once + restarts
 * the interval as soon as it becomes visible again.
 *
 * Cheaper than `setInterval` alone because it stops eating network,
 * CPU and re-renders when the user isn't looking at the page.
 */
export function useVisiblePolling(fn, intervalMs, deps = []) {
  const savedFn = useRef(fn);
  savedFn.current = fn;

  useEffect(() => {
    let timerId = null;
    const clear = () => {
      if (timerId) {
        clearInterval(timerId);
        timerId = null;
      }
    };
    const start = () => {
      clear();
      timerId = setInterval(() => { savedFn.current?.(); }, intervalMs);
    };
    const onVis = () => {
      if (document.hidden) {
        clear();
      } else {
        savedFn.current?.();
        start();
      }
    };
    // Kick off + start.
    savedFn.current?.();
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default useVisiblePolling;
